import { BrazeManager } from '@utils/brazeManager';
import { Product as DisplayProduct } from '@wconcept/wconcept-common/models/display/product';
import {
  DiscoveryItem,
  DiscoveryTabContentType,
} from '@wconcept/wconcept-common/models/renewal/discovery';
import { ProfileInfo } from '@wconcept/wconcept-common/models/renewal/display';
import { Product as RenewalProduct } from '@wconcept/wconcept-common/models/renewal/product';
import { useCallback } from 'react';
import useGlobalState from './useGlobalState';

function useBrazeLogger() {
  const { appInfo, deviceInfo, domainGender } = useGlobalState();

  const logCustomEvent = async (props: {
    eventName: string;
    properties?: {
      [key: string]: string | number | string[] | number[] | undefined;
    };
  }) => {
    const properties = props.properties
      ? Object.fromEntries(
          Object.entries(props.properties)
            .filter(([, value]) => value !== undefined)
            .map(([key, value]) => [key, value as string | number | string[]])
        )
      : undefined;
    console.log(
      '###logCustomEvent eventName',
      JSON.stringify({
        name: props.eventName,
        properties,
      })
    );
    return BrazeManager.logEvent({
      eventName: props.eventName,
      isNativeAppBoth: appInfo?.isNativeAppBoth,
      isIOS: appInfo?.isIos,
      properties,
    });
  };

  const sendBrazeDisplayMyHeart = (props: {
    product: DisplayProduct;
    pageId: string;
  }) => {
    const gender = domainGender.toLowerCase();
    const eventParams = {
      category_Name1: props.product?.mediumName,
      category_Name2: props.product?.categoryDepthName1,
      category_Name3: props.product?.categoryDepthName2,
      category_Name4: props.product?.categoryDepthName3,
      discount_rate: props.product.finalDiscountRate,
      price: props.product.customerPrice,
      discount_price: props.product.finalPrice,
      itemName: props.product.itemName,
      itemCode: props.product.itemCd,
      brandCode: props.product.brandCd,
      brandName: props.product.brandNameEn,
    };
    logCustomEvent({
      eventName: `click_${gender}_${props.pageId}_list_myheart`,
      properties: eventParams,
    });
  };

  const sendBrazeRenewalMyHeart = (props: {
    product: RenewalProduct;
    pageId: string;
    useGender?: boolean;
    defaultGender?: string;
  }) => {
    const gender = props?.defaultGender ? 'women' : domainGender.toLowerCase();
    const genderValue = props?.useGender ? `_${gender}` : '';
    const eventParams = {
      category_Name1: props.product?.mediumName,
      category_Name2: props.product?.categoryDepthName1,
      category_Name3: props.product?.categoryDepthName2,
      category_Name4: props.product?.categoryDepthName3,
      discount_rate: props.product.finalDiscountRate,
      price: props.product.customerPrice,
      discount_price: props.product.finalPrice,
      itemName: props.product.itemName,
      itemCode: props.product.itemCd,
      brandCode: props.product.brandCd,
      brandName: props.product.brandNameEn,
    };
    logCustomEvent({
      eventName: `click${genderValue}_${props.pageId}_list_myheart`,
      properties: eventParams,
    });
  };

  const getContentCreator = (profileInfo?: ProfileInfo) => {
    if (profileInfo?.profileType === 'CUSTOMER') {
      return profileInfo.profile.custNo;
    }
    if (profileInfo?.profileType === 'SELLER') {
      return 'brand';
    }
    if (profileInfo?.profileType === 'WCONCEPT') {
      return 'wconcept';
    }
    return '';
  };

  const getDiscoveryCreator = (content: DiscoveryItem) => {
    if (content.contentType === 'BRAND_STORY') {
      return undefined;
    }
    if (content.contentType === 'W_SERIES') {
      return undefined;
    }
    if (content.contentType === 'LOOKBOOK') {
      return undefined;
    }
    if (content.contentType === 'SHOP_LIVE') {
      return undefined;
    }
    if (content.contentType === 'STYLE_CLIP') {
      return getContentCreator(content.styleClip?.profileInfo);
    }
    return undefined;
  };

  const setCustomUserAttribute = useCallback(
    (params: { [key: string]: any }) => {
      if (window?.braze) {
        const user = window.braze.getUser();

        // params 객체의 각 키-값 쌍을 순회하면서 attribute 설정
        Object.entries(params).forEach(([key, value]) => {
          user.setCustomUserAttribute(key, value as string);
        });
      }
    },
    []
  );

  return {
    logCustomEvent,
    sendBrazeDisplayMyHeart,
    sendBrazeRenewalMyHeart,
    getContentCreator,
    getDiscoveryCreator,
    setCustomUserAttribute,
  };
}

export default useBrazeLogger;
